import {
  type InfiniteData,
  type QueryFunctionContext,
  useInfiniteQuery,
} from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import type { ConnectorInvocationStatuses } from '@/types/core/connector-invocation-status';
import type { UUID } from '@/utils/uuid';

import { connectorQueryKeys } from './query-keys';

/**
 * Hook to list all invocation statuses for a connector feature.
 * Supports paginated results through cursor-based navigation.
 */
export function useListConnectorFeatureInvocationStatuses(
  tenantId: UUID | undefined,
  connectorId: UUID | undefined,
  featureId: UUID | undefined,
  opts?: {
    limit?: number;
    enabled?: boolean;
  }
) {
  return useInfiniteQuery<
    ConnectorInvocationStatuses,
    Error,
    InfiniteData<ConnectorInvocationStatuses, unknown>
  >({
    queryKey: connectorQueryKeys.invocationStatuses(
      tenantId,
      connectorId,
      featureId,
      opts?.limit
    ),
    queryFn: (context: QueryFunctionContext) => {
      if (!tenantId || !connectorId || !featureId) {
        throw new Error('Missing tenantId, connectorId, or featureId');
      }
      const pageParam = context.pageParam as string | undefined;
      return connectorService.listConnectorInvocationStatuses(
        tenantId,
        connectorId,
        featureId,
        {
          limit: opts?.limit,
          next: pageParam,
        }
      );
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    getPreviousPageParam: (firstPage) => firstPage.prev ?? undefined,
    enabled:
      opts?.enabled !== false && !!tenantId && !!connectorId && !!featureId,
  });
}
